// Componenti e costanti condivise dello step Sopralluogo (badge stato, callout, sezione collassabile)
import { useState } from 'react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { CalendarBlank, CalendarCheck, CheckCircle, CaretDown, CaretUp } from '@phosphor-icons/react';

// Status configuration with colors and icons
export const STATUS_CONFIG = {
    DA_PIANIFICARE: {
        label: 'Da Pianificare',
        color: 'bg-amber-100 text-amber-800 border-amber-300',
        icon: CalendarBlank,
        description: 'Il sopralluogo deve essere pianificato con data e tecnico'
    },
    PIANIFICATO: {
        label: 'Pianificato',
        color: 'bg-blue-100 text-blue-800 border-blue-300',
        icon: CalendarCheck,
        description: 'Sopralluogo pianificato - esegui i rilievi e compila l\'esito'
    },
    COMPLETATO: {
        label: 'Completato',
        color: 'bg-green-100 text-green-800 border-green-300',
        icon: CheckCircle,
        description: 'Sopralluogo completato con successo'
    }
};

// Survey outcome options
export const SURVEY_OUTCOMES = [
    { value: 'POSITIVO', label: 'Positivo - Installazione fattibile', color: 'text-green-600' },
    { value: 'POSITIVO_CON_NOTE', label: 'Positivo con note - Fattibile con accorgimenti', color: 'text-blue-600' },
    { value: 'CRITICITA_DA_VERIFICARE', label: 'Criticità da verificare - Richiede approfondimenti', color: 'text-amber-600' },
    { value: 'NEGATIVO', label: 'Negativo - Installazione non consigliata', color: 'text-red-600' }
];

// Time slot options
export const TIME_SLOTS = [
    { value: 'mattina_presto', label: 'Mattina presto (8:00-10:00)' },
    { value: 'mattina', label: 'Mattina (10:00-12:00)' },
    { value: 'primo_pomeriggio', label: 'Primo pomeriggio (14:00-16:00)' },
    { value: 'tardo_pomeriggio', label: 'Tardo pomeriggio (16:00-18:00)' }
];

// StatusBadge component
export const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.DA_PIANIFICARE;
    const Icon = config.icon;
    
    return (
        <Badge className={`${config.color} border flex items-center gap-1.5 px-3 py-1`}>
            <Icon weight="fill" className="h-4 w-4" />
            {config.label}
        </Badge>
    );
};

// Alert callout based on status
export const StatusCallout = ({ status, missingFields = [] }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.DA_PIANIFICARE;
    const Icon = config.icon;
    
    let alertVariant = 'default';
    if (status === 'DA_PIANIFICARE') alertVariant = 'warning';
    if (status === 'COMPLETATO') alertVariant = 'success';
    
    return (
        <Alert className={`mb-4 ${
            status === 'DA_PIANIFICARE' ? 'border-amber-300 bg-amber-50' :
            status === 'PIANIFICATO' ? 'border-blue-300 bg-blue-50' :
            'border-green-300 bg-green-50'
        }`}>
            <Icon className="h-4 w-4" />
            <AlertTitle className="text-sm font-medium">{config.label}</AlertTitle>
            <AlertDescription className="text-xs mt-1">
                {config.description}
                {missingFields.length > 0 && (
                    <div className="mt-2 text-red-600">
                        <strong>Campi mancanti:</strong> {missingFields.join(', ')}
                    </div>
                )}
            </AlertDescription>
        </Alert>
    );
};

// Section component with collapsible
export const FormSection = ({ 
    title, 
    description, 
    icon: Icon, 
    children, 
    defaultOpen = true,
    disabled = false,
    badge = null
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    
    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mb-4">
            <Card className={disabled ? 'opacity-60' : ''}>
                <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors py-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {Icon && <Icon weight="fill" className="h-5 w-5 text-primary" />}
                                <div>
                                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                                        {title}
                                        {badge}
                                    </CardTitle>
                                    {description && (
                                        <CardDescription className="text-xs mt-0.5">{description}</CardDescription>
                                    )}
                                </div>
                            </div>
                            {isOpen ? <CaretUp className="h-4 w-4" /> : <CaretDown className="h-4 w-4" />}
                        </div>
                    </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <CardContent className="pt-0">
                        {disabled ? (
                            <p className="text-sm text-muted-foreground py-4 text-center">
                                Questa sezione sarà disponibile dopo la pianificazione
                            </p>
                        ) : children}
                    </CardContent>
                </CollapsibleContent>
            </Card>
        </Collapsible>
    );
};
